import { whatsappLink } from "@/lib/clinic";
import { ArrowUpRight } from "lucide-react";

const PILLARS = [
  {
    title: "Listen first",
    body: "Every consultation begins with understanding your story — not just your symptoms.",
  },
  {
    title: "Hands-on therapy",
    body: "Manual techniques, mobilisation and dry needling — delivered by trained physios.",
  },
  {
    title: "Move forward",
    body: "Progressive home plans so the recovery continues long after you leave the clinic.",
  },
];

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-16 md:py-32 lg:py-40 bg-sand-100"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-12 gap-8 md:gap-12 items-center">
          {/* Image left, asymmetric */}
          <div className="col-span-12 lg:col-span-5">
            <figure className="relative">
              <div className="relative overflow-hidden rounded-[2rem] aspect-[4/5]">
                <img
                  src="https://images.unsplash.com/photo-1611073615723-e06aceba76d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHw0fHxwaHlzaW90aGVyYXB5JTIwdHJlYXRtZW50JTIwd2FybXxlbnwwfHx8fDE3NzczMDAxMTB8MA&ixlib=rb-4.1.0&q=85"
                  alt="A therapist's hands offering care"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 hidden md:block">
                <div className="rounded-3xl bg-forest-900 text-sand-50 p-6 w-56 shadow-[0_20px_50px_-20px_rgba(26,50,37,0.45)]">
                  <p className="text-xs tracking-[0.28em] uppercase opacity-70">
                    Since 2013
                  </p>
                  <p className="font-serif text-2xl mt-2 leading-tight">
                    Healing in <em className="text-terracotta-500">Mylapore</em>
                  </p>
                </div>
              </div>
            </figure>
          </div>

          {/* Text right */}
          <div className="col-span-12 lg:col-span-7 lg:pl-8">
            <span className="inline-flex items-center gap-3 text-xs tracking-[0.28em] uppercase text-forest-700">
              <span className="w-10 h-px bg-terracotta-500" />
              About Revive Ripple
            </span>
            <h2 className="mt-5 font-serif text-5xl md:text-6xl leading-[1.02] tracking-tight text-forest-900">
              A small clinic with a
              <br />
              <em className="text-terracotta-500">big-hearted</em> approach.
            </h2>
            <p className="mt-8 text-lg leading-relaxed text-forest-700/85 max-w-xl">
              We believe recovery is more than reps and routines — it's a
              relationship. From your first session, we build a programme that
              respects your pace, your pain, and your everyday life. The
              ripple grows from there.
            </p>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-forest-900/15 rounded-2xl overflow-hidden border border-forest-900/15">
              {PILLARS.map((p, i) => (
                <div
                  key={p.title}
                  data-testid={`about-pillar-${i}`}
                  className="bg-sand-100 p-6 md:p-7"
                >
                  <p className="text-xs tracking-[0.28em] uppercase text-terracotta-500">
                    0{i + 1}
                  </p>
                  <h3 className="font-serif text-2xl mt-2 text-forest-900">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm text-forest-700/85 leading-relaxed">
                    {p.body}
                  </p>
                </div>
              ))}
            </div>

            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-cta-btn"
              className="mt-10 inline-flex items-center gap-3 text-forest-900 group"
            >
              <span className="font-serif italic text-2xl">
                Start a conversation
              </span>
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-forest-900/30 group-hover:bg-terracotta-500 group-hover:border-terracotta-500 group-hover:text-sand-50 transition-all">
                <ArrowUpRight size={18} />
              </span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
